import dayjs from "dayjs"
import { numberFormat } from "utils"
import { pettyCashColNm } from "./constant"
import { whtPayrTypOptions } from "constants/subcontractConstant"

// Turns the rows of /petty-cash/getPettyCashLog into timeline events for PettyCashLogDialog.
// Log rows are full snapshots (A/E = new values, D = values before delete), so every
// change is found by comparing a row with the previous row of the same record.

const fmtDateTime = (v) => v ? dayjs(v).format("DD/MM/YYYY HH:mm") : ""
const fmtDate = (v) => v ? dayjs(v).format("DD/MM/YYYY") : ""
const fmtNum = (v) => v === null || v === undefined || v === "" ? "" : numberFormat(+v)
const fmtPct = (v) => v === null || v === undefined || v === "" ? "" : `${+v}%`
const fmtIsCost = (v) => v === 1 ? "ต้นทุน" : v === 0 ? "รายได้" : ""
const fmtIsOwnRcpt = (v) => v === 1 ? "บริษัท" : v === 0 ? "ลูกค้า" : ""
const fmtInOut = (v) => v === "O" ? "จ่าย" : v === "I" ? "รับ" : ""
const fmtWhtPayrTyp = (v) => v ? whtPayrTypOptions.find(o => o.value === v)?.label || v : ""
const fmtYesNo = (v) => v ? "ใช่" : "ไม่ใช่"
const fmtRef = (v) => v ? `#${v}` : ""

// [field, label, formatter] — only what a user would recognise; ids are shown by name
const TRNS_FIELDS = [
  ["UsrNNm", pettyCashColNm.UsrNNm],
  ["InOutTyp", pettyCashColNm.InOutTyp, fmtInOut],
  ["ExpNm", pettyCashColNm.ExpNm],
  ["PCJnlNm", pettyCashColNm.PCJnlNm],
  ["PayTm", pettyCashColNm.PayTm, fmtDateTime],
  ["JobOrdId", pettyCashColNm.JobOrdId],
  ["Dscp", pettyCashColNm.Dscp],
  ["Amnt", pettyCashColNm.Amnt, fmtNum],
  ["VatPrct", pettyCashColNm.VatPrct, fmtPct],
  ["WhtPrct", pettyCashColNm.WhtPrct, fmtPct],
  ["IsCost", pettyCashColNm.IsCost, fmtIsCost],
  ["IsOwnRcpt", "ใบเสร็จ บริษัท/ลูกค้า", fmtIsOwnRcpt],
  ["SupSNm", pettyCashColNm.SupSNm],
  ["WhtTypNm", pettyCashColNm.WhtTypNm],
  ["WhtPayrTyp", pettyCashColNm.WhtPayrTyp, fmtWhtPayrTyp],
  ["IsShowPrintWht", "พิมพ์หัก ณ ที่จ่าย", fmtYesNo],
  ["CtrlCd", pettyCashColNm.CtrlCd],
  ["PCPrdId", "ยืนยันยอด", fmtRef],
  ["Rmk", pettyCashColNm.Rmk],
]

const ITEM_FIELDS = [
  ["PCItmTm", pettyCashColNm.ItmDte, fmtDateTime],
  ["ExpNm", pettyCashColNm.ExpNm],
  ["Dscp", pettyCashColNm.Dscp],
  ["DocNo", pettyCashColNm.DocNo],
  ["DocDte", pettyCashColNm.DocDte, fmtDate],
  ["JobOrdId", pettyCashColNm.JobOrdId],
  ["Amnt", pettyCashColNm.Amnt, fmtNum],
  ["VatPrct", pettyCashColNm.VatPrct, fmtPct],
  ["WhtPrct", pettyCashColNm.WhtPrct, fmtPct],
  ["InOutTypItm", pettyCashColNm.InOutTyp, fmtInOut],
  ["IsCost", "รายได้/ต้นทุน", fmtIsCost],
  ["IsOwnRcpt", "ใบเสร็จ บริษัท/ลูกค้า", fmtIsOwnRcpt],
  ["SupSNm", pettyCashColNm.SupSNm],
  ["ScSNm", pettyCashColNm.ScSNm],
  ["WhtTypNm", pettyCashColNm.WhtTypNm],
  ["WhtPayrTyp", pettyCashColNm.WhtPayrTyp, fmtWhtPayrTyp],
  ["IsShowPrintWht", "พิมพ์หัก ณ ที่จ่าย", fmtYesNo],
  ["CtrlCd", pettyCashColNm.CtrlCd],
  ["RefPCTrnsId", "Transaction อ้างอิง", fmtRef],
  ["Rmk", pettyCashColNm.Rmk],
]

// Checkboxes of the dialog footer; IsCmpt / IsRdyXpss are also copied onto every child
// transaction, so they are only reported on the main one
const FLAG_FIELDS = [
  ["IsCmpt", pettyCashColNm.IsCmpt],
  ["IsRdyXpss", pettyCashColNm.IsRdyXpss],
]

// Same user within this many seconds = one save
const SAME_SAVE_SEC = 2
// Inline cell edits save one field at a time; consecutive pure edits are merged within this window
const SAME_EDIT_SESSION_SEC = 60

const fmtField = (row, [field, , fmt]) => {
  const v = row?.[field]
  if (fmt) return fmt(v)
  return v === null || v === undefined ? "" : `${v}`
}

const diffFields = (prev, row, fields) => {
  const diffs = []
  for (const f of fields) {
    const from = fmtField(prev, f)
    const to = fmtField(row, f)
    if (from !== to) diffs.push({ field: f[0], label: f[1], from, to })
  }
  return diffs
}

const diffFlags = (prev, row) => {
  const flags = []
  for (const [field, label] of FLAG_FIELDS) {
    const from = prev?.[field] ? 1 : 0
    const to = row?.[field] ? 1 : 0
    // column missing before the log migration → undefined on both sides, never a change
    if (row?.[field] === undefined) continue
    if (from !== to) flags.push({ field, label, from, to })
  }
  return flags
}

export const snapshotFields = (kind, row) => (kind === "T" ? TRNS_FIELDS : ITEM_FIELDS)
  .map(f => ({ label: f[1], value: fmtField(row, f) }))
  .filter(f => f.value !== "")

const summaryText = (kind, row) => {
  const parts = kind === "T"
    ? [row.UsrNNm, fmtInOut(row.InOutTyp), row.PCJnlNm && `บัญชี: ${row.PCJnlNm}`]
    : [row.Dscp, row.DocNo && `เลขที่ ${row.DocNo}`]
  const amnt = row.Amnt !== null && row.Amnt !== undefined ? `${fmtNum(row.Amnt)} บาท` : null
  return [...parts, amnt].filter(Boolean).join(" · ")
}

const recordLabel = (kind, row, mainId) => {
  if (kind === "T") {
    return row.PCTrnsId === mainId ? `การจ่ายเงินหลัก #${row.PCTrnsId}` : `${row.Dscp || row.ExpNm || "รายการ"} #${row.PCTrnsId}`
  }
  return `${row.ExpNm || "รายการ"} #${row.PCItmId}`
}

const makeChange = ({ kind, row, prev, inScope, mainId }) => {
  const id = kind === "T" ? row.PCTrnsId : row.PCItmId
  const change = {
    kind,
    key: `${kind}${id}`,
    logId: kind === "T" ? row.PCTrnsLogId : row.PCItmLogId,
    time: dayjs(row.ModDte),
    accId: row.ModAccId ?? 0,
    accNm: row.ModAccNm || (row.ModAccId ? `AccId ${row.ModAccId}` : "ระบบ"),
    // LogRmk stays on the row after an insert, so only a new value belongs to this save
    logRmk: row.LogRmk && (row.LogFlg !== "E" || prev?.LogRmk !== row.LogRmk) ? row.LogRmk : "",
    label: recordLabel(kind, row, mainId),
    summary: summaryText(kind, row),
    row,
    diffs: [],
    flags: [],
  }
  const fields = kind === "T" ? TRNS_FIELDS : ITEM_FIELDS
  if (row.LogFlg === "A") return { ...change, type: "add" }
  if (row.LogFlg === "D") return { ...change, type: "delete" }
  // E without an earlier row: history before the log trigger existed, nothing to compare
  if (!prev) return { ...change, type: "noop" }

  change.diffs = diffFields(prev, row, fields)
  if (kind === "T" && row.PCTrnsId === mainId) change.flags = diffFlags(prev, row)

  const wasIn = inScope(prev)
  const isIn = inScope(row)
  if (wasIn && !isIn) {
    const target = kind === "T"
      ? (row.PrPCTrnsId ? `#${row.PrPCTrnsId}` : "การจ่ายเงินหลักใหม่")
      : (row.PCTrnsId ? `#${row.PCTrnsId}` : "ไม่ผูกกับการจ่ายเงิน")
    return { ...change, type: "moveOut", target }
  }
  if (!wasIn && isIn) {
    const source = kind === "T"
      ? (prev.PrPCTrnsId ? `#${prev.PrPCTrnsId}` : "การจ่ายเงินหลักเดิม")
      : (prev.PCTrnsId ? `#${prev.PCTrnsId}` : "ใบเสร็จที่ยังไม่ผูก")
    return { ...change, type: "moveIn", source }
  }
  return { ...change, type: change.diffs.length || change.flags.length ? "edit" : "noop" }
}

const groupByKey = (rows, key) => {
  const map = new Map()
  for (const r of rows) {
    if (!map.has(r[key])) map.set(r[key], [])
    map.get(r[key]).push(r)
  }
  return map
}

// Merge several diffs of the same field: keep the first "from" and the last "to"
const mergeDiffList = (list) => {
  const byField = new Map()
  for (const d of list) {
    const found = byField.get(d.field)
    byField.set(d.field, found ? { ...found, to: d.to } : { ...d })
  }
  return [...byField.values()].filter(d => d.from !== d.to)
}

// Within one event, fold repeated edits of the same record into one line
const mergeEventChanges = (changes) => {
  const result = []
  const editByKey = new Map()
  for (const c of changes) {
    if (c.type !== "edit" && c.type !== "noop") {
      result.push(c)
      continue
    }
    const found = editByKey.get(c.key)
    if (!found) {
      const copy = { ...c }
      editByKey.set(c.key, copy)
      result.push(copy)
      continue
    }
    found.diffs = mergeDiffList([...found.diffs, ...c.diffs])
    found.flags = mergeDiffList([...found.flags, ...c.flags])
    found.label = c.label
    found.type = found.diffs.length || found.flags.length ? "edit" : "noop"
  }
  // a record that was added / deleted / moved in this event does not need an extra noop line
  const keysWithAction = new Set(result.filter(c => c.type !== "edit" && c.type !== "noop").map(c => c.key))
  return result.filter(c => !(c.type === "noop" && keysWithAction.has(c.key)))
}

export const buildPettyCashLogEvents = ({ PCTrnsId: mainId, pcTransactionLogs, pcItemLogs }) => {
  const trnsIds = new Set(pcTransactionLogs.map(r => r.PCTrnsId))
  const inScopeTrns = (r) => r ? (r.PCTrnsId === mainId || r.PrPCTrnsId === mainId) : false
  const inScopeItem = (r) => r ? trnsIds.has(r.PCTrnsId) : false

  const changes = []
  const collect = (kind, logs, idCol, inScope) => {
    for (const history of groupByKey(logs, idCol).values()) {
      history.forEach((row, i) => {
        const prev = history[i - 1]
        // skip what happened to the record while it belonged to another advance
        if (!inScope(row) && !inScope(prev)) return
        changes.push(makeChange({ kind, row, prev, inScope, mainId }))
      })
    }
  }
  collect("T", pcTransactionLogs, "PCTrnsId", inScopeTrns)
  collect("I", pcItemLogs, "PCItmId", inScopeItem)

  changes.sort((a, b) => a.time.valueOf() - b.time.valueOf()
    || (a.kind === b.kind ? 0 : a.kind === "T" ? -1 : 1)
    || a.logId - b.logId)

  const events = []
  for (const c of changes) {
    const last = events[events.length - 1]
    const gap = last ? c.time.diff(last.endTime, "second") : Infinity
    const isEditOnly = (x) => x.type === "edit" || x.type === "noop"
    const sameSave = last && last.accId === c.accId && gap <= SAME_SAVE_SEC
    const sameEditSession = last && last.accId === c.accId && gap <= SAME_EDIT_SESSION_SEC
      && isEditOnly(c) && last.changes.every(isEditOnly)
    if (sameSave || sameEditSession) {
      last.changes.push(c)
      last.endTime = c.time
    } else {
      events.push({ id: events.length + 1, time: c.time, endTime: c.time, accId: c.accId, accNm: c.accNm, changes: [c] })
    }
  }

  for (const e of events) {
    e.changes = mergeEventChanges(e.changes)
    e.logRmks = [...new Set(e.changes.map(c => c.logRmk).filter(Boolean))]
    const mainChange = e.changes.find(c => c.kind === "T" && c.row.PCTrnsId === mainId)
    e.tag = mainChange?.type === "add" ? "add" : mainChange?.type === "delete" ? "delete" : null
  }
  return events.reverse()
}
